import type { DPDay, DPUserConfig } from "@rehookify/datepicker";
import { useDatePicker } from "@rehookify/datepicker";
import dayjs from "dayjs";
import { type KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, isWeb, styled } from "tamagui";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { XStack, YStack } from "../Stacks";
import { Body2 } from "../Typography";
import { isCalendarNavKey, nextFocusedDate } from "./calendarKeyboard";
import { CalendarHeader, WeekView } from "./dateParts";
const DAY_CELL_SIZE = "$si5.5";
const DAYS_PER_WEEK = 7;
const gridcellRole = isWeb ? {
  // biome-ignore lint/a11y/useSemanticElements: <td> is invalid outside a <table>; the grid and rows render as divs with ARIA roles
  render: <div role="gridcell" tabIndex={-1} />
} : {};
const dayIso = (date: Date): string => dayjs(date).format("YYYY-MM-DD");

/** Day-state styling as variants (not render-time ternaries) so it compiles to
 *  atomic CSS. `selected` wins over `today`; out-of-month renders an empty slot.
 *  Grid cells pair `flex={1}` with `flexBasis={0}`: Tamagui's `flex` leaves
 *  basis `auto`, which lets empty filler cells collapse below day cells and
 *  drifts partial weeks off their weekday columns. */
const DayCell = styled(XStack, {
  alignItems: "center",
  justifyContent: "center",
  width: DAY_CELL_SIZE,
  height: DAY_CELL_SIZE,
  borderRadius: "$brFull",
  borderWidth: "$border3",
  borderColor: "transparent",
  // On the base, not the `interactive` variant: a roving-focused disabled cell
  // must still show the focus ring even though it rejects press.
  focusVisibleStyle: {
    outlineWidth: "$border3",
    outlineColor: "$borderIntense",
    outlineStyle: "solid"
  },
  variants: {
    today: {
      true: {
        borderColor: "$borderIntense"
      }
    },
    selected: {
      true: {
        backgroundColor: "$surfaceStrong",
        borderColor: "$surfaceStrong"
      }
    },
    interactive: {
      true: {
        cursor: "pointer",
        hoverStyle: {
          backgroundColor: "$surface2Hover"
        },
        pressStyle: {
          backgroundColor: "$surface0Hover"
        }
      }
    },
    disabled: {
      true: {
        cursor: "default"
      }
    }
  } as const
});
const DayLabel = styled(Body2, {
  color: "$textDefault",
  variants: {
    selected: {
      true: {
        color: "$textInverse",
        fontWeight: "600"
      }
    },
    disabled: {
      true: {
        color: "$textMuted"
      }
    }
  } as const
});
const dayAccessibleLabel = (date: Date): string => dayjs(date).format("dddd, MMMM D, YYYY");
export type CalendarProps = {
  /** Controlled selection, day-granularity. */
  value: Date | null;
  /** The roving-focus date; its month is the one displayed. */
  focusedDate: Date;
  onFocusedDateChange: (date: Date) => void;
  /** Drives focus-on-open: the focused cell takes focus when this flips true
   *  (the caller re-anchors `focusedDate` on the same transition). */
  open: boolean;
  onSelect: (date: Date) => void;
  min?: Date;
  max?: Date;
  /** Caller predicate for arbitrary disabled days (e.g. weekends). Combined with
   *  rehookify's own min/max gating. */
  isDateDisabled?: (date: Date) => boolean;
  /** 0 = Sunday (default), 1 = Monday. */
  weekStartsOn?: 0 | 1;
  /** Accessible label for the grid container. @default "Choose date" */
  label?: string;
};
type DayButtonProps = {
  day: DPDay;
  /** True when the day is disabled by min/max OR the caller predicate. */
  disabled: boolean;
  /** The single roving tab stop — focusable even when disabled. */
  focused: boolean;
  id: string;
  onSelect: (date: Date) => void;
};
const DayButton = ({
  day,
  disabled,
  focused,
  id,
  onSelect
}: DayButtonProps) => {
  const handlePress = useCallback(() => {
    onSelect(day.$date);
  }, [day.$date, onSelect]);
  const {
    interactiveProps
  } = useInteractiveElement({
    kind: "button",
    onPress: handlePress,
    disabled,
    haptic: true,
    tabIndex: focused ? 0 : -1
  });
  return <XStack {...gridcellRole} aria-selected={day.selected} flex={1} flexBasis={0} alignItems="center" justifyContent="center" data-sentry-element="XStack" data-sentry-component="DayButton" data-sentry-source-file="Calendar.tsx">
      <DayCell id={id} role="button" aria-label={dayAccessibleLabel(day.$date)} aria-current={day.now ? "date" : undefined} today={day.now} selected={day.selected} disabled={disabled} interactive={!disabled} {...interactiveProps} data-sentry-element="DayCell" data-sentry-source-file="Calendar.tsx">
        <DayLabel selected={day.selected} disabled={disabled} data-sentry-element="DayLabel" data-sentry-source-file="Calendar.tsx">
          {day.day}
        </DayLabel>
      </DayCell>
    </XStack>;
};
const weekKey = (week: DPDay[]): string => week[0] ? week[0].$date.toISOString() : "empty";

/**
 * Controlled month grid with the WAI-ARIA APG grid keyboard model (single roving
 * tab stop, arrow/Home/End/Page navigation, focus-on-open). `@rehookify/datepicker`
 * supplies the date model; the displayed month follows `focusedDate`.
 */
export const Calendar = ({
  value,
  focusedDate,
  onFocusedDateChange,
  open,
  onSelect,
  min,
  max,
  isDateDisabled,
  weekStartsOn = 0,
  label = "Choose date"
}: CalendarProps) => {
  const config: DPUserConfig = {
    selectedDates: value ? [value] : [],
    // The grid is render-only here; selection is handled by the caller's press.
    onDatesChange: () => {},
    offsetDate: focusedDate,
    dates: {
      minDate: min,
      maxDate: max,
      mode: "single",
      toggle: false
    },
    calendar: {
      startDay: weekStartsOn
    }
  };
  const {
    data: {
      calendars,
      weekDays
    }
  } = useDatePicker(config);
  const calendar = calendars[0];

  // Bucket the flat day list into weeks for the grid layout (Bento's reduce).
  const weeks = useMemo(() => {
    const days = calendar?.days ?? [];
    return days.reduce<DPDay[][]>((acc, day, index) => {
      if (index % DAYS_PER_WEEK === 0) {
        acc.push([]);
      }
      acc[acc.length - 1]?.push(day);
      return acc;
    }, []);
  }, [calendar?.days]);
  const gridId = useId();
  const cellId = useCallback((date: Date) => `${gridId}-${dayIso(date)}`, [gridId]);

  // Read the live focused date inside handlers/effects without re-arming their
  // deps, so a parent re-render can't stale the keyboard math.
  const focusedDateRef = useRef(focusedDate);
  focusedDateRef.current = focusedDate;

  // Bumped on keyboard nav / open so the focus effect refires even when the date is
  // unchanged; chevron clicks leave it alone so focus stays on the chevron.
  const [focusTick, setFocusTick] = useState(0);
  const handleGridKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!isCalendarNavKey(event.key)) {
      return;
    }
    event.preventDefault();
    onFocusedDateChange(nextFocusedDate(focusedDateRef.current, event.key, weekStartsOn));
    setFocusTick(tick => tick + 1);
  }, [onFocusedDateChange, weekStartsOn]);
  const handlePreviousMonth = useCallback(() => {
    onFocusedDateChange(dayjs(focusedDateRef.current).subtract(1, "month").toDate());
  }, [onFocusedDateChange]);
  const handleNextMonth = useCallback(() => {
    onFocusedDateChange(dayjs(focusedDateRef.current).add(1, "month").toDate());
  }, [onFocusedDateChange]);
  useEffect(() => {
    if (open) {
      setFocusTick(tick => tick + 1);
    }
  }, [open]);
  useLayoutEffect(() => {
    if (!isWeb || focusTick === 0) {
      return;
    }
    document.getElementById(cellId(focusedDateRef.current))?.focus();
  }, [focusTick, cellId]);
  if (!calendar) {
    return null;
  }
  const monthKey = `${calendar.year}-${calendar.month}`;
  return <YStack gap="$sp1.5" padding="$sp1" data-sentry-element="YStack" data-sentry-component="Calendar" data-sentry-source-file="Calendar.tsx">
      <CalendarHeader monthLabel={calendar.month} yearLabel={calendar.year} onPreviousMonth={handlePreviousMonth} onNextMonth={handleNextMonth} data-sentry-element="CalendarHeader" data-sentry-source-file="Calendar.tsx" />
      <WeekView weekDays={weekDays} data-sentry-element="WeekView" data-sentry-source-file="Calendar.tsx" />
      {/* Key the AnimatePresence (not the child) so a month change unmounts the
          old grid outright; keying the child kept both months mounted and the
          exiting one stacked below the entering one in this vertical parent. */}
      <AnimatePresence key={monthKey} data-sentry-element="AnimatePresence" data-sentry-source-file="Calendar.tsx">
        <YStack role="grid" aria-label={label} onKeyDown={handleGridKeyDown} gap="$sp0.5" opacity={1} enterStyle={{
        opacity: 0
      }} transition="fade" animateOnly={["opacity"]} data-sentry-element="YStack" data-sentry-source-file="Calendar.tsx">
          {weeks.map(week => week.some(day => day.inCurrentMonth) ? <XStack key={weekKey(week)} role="row" width="100%">
                {week.map(day => day.inCurrentMonth ? <DayButton key={day.$date.toISOString()} day={day} disabled={day.disabled || (isDateDisabled?.(day.$date) ?? false)} focused={dayjs(day.$date).isSame(focusedDate, "day")} id={cellId(day.$date)} onSelect={onSelect} /> : <XStack key={day.$date.toISOString()} {...gridcellRole} flex={1} flexBasis={0} />)}
              </XStack> : null)}
        </YStack>
      </AnimatePresence>
    </YStack>;
};